import { useEffect } from 'react';

const SITE = 'https://vendorsuite.io';
const DEFAULT_IMAGE = `${SITE}/og-image.png`;
const DEFAULT_DESC =
  'AI-powered vendor compliance automation for property managers. Track COIs, W-9s and licenses, auto-chase expiring documents, and score compliance across your portfolio.';

// Create or update a <meta>/<link> tag identified by a selector.
function upsert(tag, attrs, keyAttr, keyVal) {
  let el = document.head.querySelector(`${tag}[${keyAttr}="${keyVal}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(keyAttr, keyVal);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

/**
 * Per-page SEO without any dependency. Sets title, description, canonical,
 * Open Graph, Twitter card, and optional JSON-LD. Runs on mount/update; because
 * it writes to the DOM after render, prerenderers (react-snap) capture it too.
 */
export const Seo = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  jsonLd,
}) => {
  const url = `${SITE}${path}`;
  const fullTitle = title
    ? `${title} | VendorSuite`
    : 'VendorSuite — Vendor Compliance Automation for Property Managers';
  const desc = description || DEFAULT_DESC;

  useEffect(() => {
    document.title = fullTitle;

    upsert('meta', { content: desc }, 'name', 'description');
    upsert('link', { href: url }, 'rel', 'canonical');

    upsert('meta', { content: type }, 'property', 'og:type');
    upsert('meta', { content: 'VendorSuite' }, 'property', 'og:site_name');
    upsert('meta', { content: fullTitle }, 'property', 'og:title');
    upsert('meta', { content: desc }, 'property', 'og:description');
    upsert('meta', { content: url }, 'property', 'og:url');
    upsert('meta', { content: image }, 'property', 'og:image');

    upsert('meta', { content: 'summary_large_image' }, 'name', 'twitter:card');
    upsert('meta', { content: fullTitle }, 'name', 'twitter:title');
    upsert('meta', { content: desc }, 'name', 'twitter:description');
    upsert('meta', { content: image }, 'name', 'twitter:image');

    // Per-page JSON-LD (id lets us replace it on navigation)
    const existing = document.getElementById('page-jsonld');
    if (existing) existing.remove();
    if (jsonLd) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = 'page-jsonld';
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [fullTitle, desc, url, image, type, jsonLd]);

  return null;
};

export default Seo;
